import React, { useState, useEffect } from 'react'
import { useSelector } from 'react-redux'
import { useTheme } from '@mui/material'
import Box from '@mui/material/Box'
import Stack from '@mui/material/Stack'
import IconButton from '@mui/material/IconButton'
import AddCircleIcon from '@mui/icons-material/AddCircle'
import DeleteIcon from '@mui/icons-material/Delete'
import defaultTexts from '../../texts/texts'
import constants from '../../config/constants'
import { request } from '../../_network/request'
import { alertsURLs } from '../../_network/apiUrls'
import { coreTypes, openStackAdminTypes, iSenderTypes } from './_common/notificationTypes'
import CustomDialog from '../_common/CustomDialog'
import CustomBackdrop from '../_common/CustomBackdrop'
import NotificationEmailAdd from './NotificationEmailAdd'

const NotificationSettings = () => {
  const theme = useTheme()

  const user_role = useSelector(state => state.profile.user_role)
  const editPermission = user_role === 'superadmin' || user_role === 'owner' || user_role === 'admin'

  const [addEmailDialogOpen, setAddEmailDialogOpen] = useState(false)
  const [emails, setEmails] = useState([])
  const [fetchEmails, setFetchEmails] = useState(1)
  const [group, setGroup] = useState(null)
  const [isLoading, setIsLoading] = useState(false)
  
  const labelStyle = {
    color: theme.palette.grayX11,
    fontSize: '18px',
    fontWeight: '500',
  }

  const typeStyle = {
    color: theme.palette.grayX11,
    fontSize: '16px',
    fontWeight: '400',
  }

  const emailStyle = {
    color: theme.palette.oxfordBlue,
    fontSize: '18px',
    fontWeight: '500',
  }

  const getTypeLabel = (group, type) => {
    let types = coreTypes
    
    if (group === 'openstack_admin') {
      types = openStackAdminTypes
    }

    if (group === 'isender') {
      types = iSenderTypes
    }

    const type_object = types.find(item => item.value === type)
    return type_object ? type_object.keyword : ''
  }

  const handleAddEmailDialogOpen = (group) => {
    setAddEmailDialogOpen(true)
    setGroup(group)
  }

  const handleAddEmailDialogClose = () => {
    setAddEmailDialogOpen(false)
    setGroup(null)
  }

  const emailRequest = async ({ method, data, record_id }) => {
    const url = record_id ?
    constants.core_service_domain + '/' + alertsURLs.getAlertsEmailSettings + '/' + record_id :
    constants.core_service_domain + '/' + alertsURLs.getAlertsEmailSettings
    const request_data = data ? { url, method, data } : { url, method }
    await request(request_data)
  }

  const handleDeleteEmail = async (email, type, group) => {
    setIsLoading(true)

    const email_object = emails.find(item => item.group === group && item.type === type && item.emails.includes(email))

    if (email_object) {
      const new_emails = email_object.emails.filter(item => item !== email)

      if (new_emails.length > 0) {
        const postData = {
          emails: new_emails, 
        }

        await emailRequest({ method: 'PUT', data: postData, record_id: email_object.id })
      } else {
        await emailRequest({ method: 'DELETE', record_id: email_object.id })
      }

      setFetchEmails(prev => prev + 1)
    }

    setIsLoading(false)
  }

  useEffect(() => {
    const fetchEmailsData = async () => {
      setIsLoading(true)

      const url = constants.core_service_domain + '/' + alertsURLs.getAlertsEmailSettings
      
      const request_data = { url, method: 'GET' }
      
      const response = await request(request_data)

      if (response.status_code && response.status_code < 400 && response.data && response.data.length > 0) {
        setEmails(response.data)
      } else {
        setEmails([])
      }

      setIsLoading(false)
    }

    fetchEmailsData()
  }, [fetchEmails])

  return (
    <>
      { isLoading && <CustomBackdrop open={isLoading} /> }

      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        spacing={1}
        sx={{
          borderBottom: `${theme.palette.grayX11} 1px solid`,
          paddingBottom: '20px',
        }}
      >
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-start'
            spacing={1}
          >
            <Box sx={labelStyle}>{defaultTexts.accountNotifications}</Box>
            {
              editPermission &&
              <IconButton
                onClick={() => handleAddEmailDialogOpen('core')}
                sx={{ color: theme.palette.grayX11 }}
              >
                <AddCircleIcon />
              </IconButton>
            }            
          </Stack>
        </Box>
        <Box>
          {
            emails.map((item, index) => (
              <Box key={index}>
                {
                  item.group === 'core' && item.emails.map((item2, index2) => (
                    <Stack
                      key={index2}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      spacing={1}
                    >
                      <Box sx={emailStyle}>
                        <Box component='span' sx={typeStyle}>{getTypeLabel('core', item.type)}: </Box>
                        <Box component='span'>{item2}</Box>
                      </Box>
                      {
                        editPermission &&
                        <IconButton
                          onClick={() => handleDeleteEmail(item2, item.type, 'core')}
                          sx={{ color: theme.palette.coralRed }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    </Stack>
                  ))
                }
              </Box>
            ))
          }
        </Box>
      </Stack>

      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        spacing={1}
        sx={{
          borderBottom: `${theme.palette.grayX11} 1px solid`,
          padding: '20px 0px',
        }}
      >
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-start'
            spacing={1}
          >
            <Box sx={labelStyle}>{defaultTexts.openStackSolutionKit} {defaultTexts.notifications}</Box>
            {
              editPermission &&
              <IconButton
                onClick={() => handleAddEmailDialogOpen('openstack_admin')}
                sx={{ color: theme.palette.grayX11 }}
              >
                <AddCircleIcon />
              </IconButton>
            }            
          </Stack>
        </Box>
        <Box>
          {
            emails.map((item, index) => (
              <Box key={index}>
                {
                  item.group === 'openstack_admin' && item.emails.map((item2, index2) => (
                    <Stack
                      key={index2}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      spacing={1}
                    >
                      <Box sx={emailStyle}>
                        <Box component='span' sx={typeStyle}>{getTypeLabel('openstack_admin', item.type)}: </Box>
                        <Box component='span'>{item2}</Box>
                      </Box>
                      {
                        editPermission &&
                        <IconButton
                          onClick={() => handleDeleteEmail(item2, item.type, 'openstack_admin')}
                          sx={{ color: theme.palette.coralRed }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    </Stack>
                  ))
                }
              </Box>
            ))
          }
        </Box>
      </Stack>

      <Stack
        direction='row'
        alignItems='flex-start'
        justifyContent='space-between'
        spacing={1}
        sx={{
          paddingTop: '20px',
        }}
      >
        <Box>
          <Stack
            direction='row'
            alignItems='center'
            justifyContent='flex-start'
            spacing={1}
          >
            <Box sx={labelStyle}>{defaultTexts.iSender} {defaultTexts.notifications}</Box>
            {
              editPermission &&
              <IconButton
                onClick={() => handleAddEmailDialogOpen('isender')}
                sx={{ color: theme.palette.grayX11 }}
              >
                <AddCircleIcon />
              </IconButton>
            }            
          </Stack>
        </Box>
        <Box>
          {
            emails.map((item, index) => (
              <Box key={index}>
                {
                  item.group === 'isender' && item.emails.map((item2, index2) => (
                    <Stack
                      key={index2}
                      direction='row'
                      alignItems='center'
                      justifyContent='space-between'
                      spacing={1}
                    >
                      <Box sx={emailStyle}>
                        <Box component='span' sx={typeStyle}>{getTypeLabel('isender', item.type)}: </Box>
                        <Box component='span'>{item2}</Box>
                      </Box>
                      {
                        editPermission &&
                        <IconButton
                          onClick={() => handleDeleteEmail(item2, item.type, 'isender')}
                          sx={{ color: theme.palette.coralRed }}
                        >
                          <DeleteIcon />
                        </IconButton>
                      }
                    </Stack>
                  ))
                }
              </Box>
            ))
          }
        </Box>
      </Stack>

      <CustomDialog
        open={addEmailDialogOpen}
        onClose={handleAddEmailDialogClose}
        dialogTitle={{
          title: defaultTexts.addEmail,
          sx: {
            color: theme.palette.violetsAreBlue,
            fontSize: '22px',
            fontWeight: '500',
          },
        }}
        closeIconColor={theme.palette.violetsAreBlue}
			>
        <NotificationEmailAdd
          group={group}
          emails={emails}
          setFetchEmails={setFetchEmails}
          emailRequest={emailRequest}
          handleAddEmailDialogClose={handleAddEmailDialogClose}
        />
      </CustomDialog>
    </>
  )
}

export default NotificationSettings